import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamCorrectExamAnsweridQueryKey,
  getApiV1ExamCorrectionGridExampublishidQueryOptions,
  useGetApiV1ExamExamItemSubTypes,
} from '@/shared/api/generated';
import type {
  ExamItemAnswerAiCorrectionViewModel,
  ExamItemAnswerTextCorrectionGridItemRepositoryResponse,
} from '@/shared/api/generated/types';
import {
  isTeachingNotesSubtype,
  MULTILINE_TEXT_COMPONENT_TYPE,
  parseKeyTerms,
} from '@/shared/lib/examItemSubtype';
import { useAiBatchSuggestions } from '../../../hooks/useAiBatchSuggestions';
import { useAiCorrection } from '../../../hooks/useAiCorrection';
import { useAnswerDetails } from '../../../hooks/useAnswerDetails';
import { useAnswerInfo } from '../../../hooks/useAnswerInfo';
import { useSubmitCorrection } from '../../../hooks/useSubmitCorrection';
import {
  buildAiCorrectionComment,
  buildCorrectionPayload,
  clampCompleteness,
  mergeAiCorrectionComment,
  normalizeAiGrade,
} from '../../../utils';

/** Estado da análise por IA de uma resposta dissertativa. */
export type AiCorrectionStatus = 'idle' | 'loading' | 'error' | 'success';

/** ID do componente de resposta em áudio em `GET /api/v1/exam/component-types`. */
const AUDIO_COMPONENT_TYPE = 7;
/** Duração da contagem animada da nota até o valor sugerido pela IA. */
const GRADE_TWEEN_DURATION_MS = 700;

/** Indica se o usuário pediu para reduzir animações no sistema operacional. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface UseCorrectionModalParams {
  /** ID da publicação do exame */
  readonly examPublishId: number | null;
  /** ID do item de questão aberto para correção */
  readonly examItemId: number | null;
  /** Item da grade com os dados da questão — fallback para o enunciado */
  readonly questionItem: ExamItemAnswerTextCorrectionGridItemRepositoryResponse | null | undefined;
  /** ID da resposta a exibir inicialmente */
  readonly initialAnswerId: number | null;
  /** Posição de `initialAnswerId` na fila de respostas da questão (1-based) */
  readonly initialPosition: number;
  /** Callback ao fechar o modal */
  readonly onClose: () => void;
}

/**
 * Hook de estado e ações do `CorrectionModal` — carregamento da resposta, fila de
 * navegação sequencial, análise por IA e envio da correção.
 *
 * Concentra toda a lógica do modal para que os componentes de UI fiquem
 * declarativos: a tela lê o que o hook devolve e chama os handlers. A conclusão da
 * análise por IA é anunciada por toast (`useAlert`), não por banner no corpo.
 *
 * @param params - Identificadores do exame/questão e cursor inicial da fila
 * @returns Estado derivado da resposta, da IA e da fila, com os handlers de ação
 *
 * @example
 * ```tsx
 * const modal = useCorrectionModal({ examPublishId, examItemId, initialAnswerId, initialPosition: 1, onClose });
 * ```
 */
export function useCorrectionModal({
  examPublishId,
  examItemId,
  questionItem,
  initialAnswerId,
  initialPosition,
  onClose,
}: UseCorrectionModalParams) {
  const { t } = useTranslation('examCorrection');
  const queryClient = useQueryClient();
  const { alert } = useAlert();

  // ID da resposta sendo exibida — fonte de verdade para o carregamento dos detalhes
  const [currentAnswerId, setCurrentAnswerId] = useState<number | null>(initialAnswerId);
  // Posição da resposta na fila da questão — cursor da navegação sequencial
  const [position, setPosition] = useState(initialPosition);
  const [completeness, setCompleteness] = useState(0);
  const [comment, setComment] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [aiStatus, setAiStatus] = useState<AiCorrectionStatus>('idle');
  const [aiResult, setAiResult] = useState<ExamItemAnswerAiCorrectionViewModel | null>(null);
  // Último bloco de devolutiva aplicado ao comentário — usado para substituí-lo
  // numa nova aplicação em vez de anexar uma segunda cópia
  const appliedAiCommentRef = useRef('');
  // Nota aplicada a partir da sugestão da IA — se o corretor alterar a completude
  // depois, a correção deixa de ser assistida por IA e a flag não é enviada
  const [appliedAiGrade, setAppliedAiGrade] = useState<number | null>(null);
  // Comentário fica somente-leitura depois que a devolutiva da IA é aplicada; só
  // volta a ser editável se o corretor alterar a completude
  const [isCommentLocked, setIsCommentLocked] = useState(false);
  const gradeTweenRef = useRef<number | null>(null);

  // Busca os detalhes completos pelo ID — nunca sofre de deslocamento de página
  const { answer: details, isLoading: isDetailsLoading } = useAnswerDetails(currentAnswerId);

  const isContentLoading = isDetailsLoading || isNavigating;
  const isAudio = details?.componentType === AUDIO_COMPONENT_TYPE;
  const isMultilineText = details?.componentType === MULTILINE_TEXT_COMPONENT_TYPE;

  // ─── Referência de correção (subtipo dissertativo) ─────────────────────────
  // A questão pode trazer resposta-modelo e termos-chave cadastrados; são os
  // mesmos critérios que a IA usa, então o corretor humano precisa vê-los.
  const subtypesQuery = useGetApiV1ExamExamItemSubTypes();
  const subtypeLabel = subtypesQuery.data?.find(o => o.key === details?.subtype)?.value;
  // `modelAnswer` é o mesmo campo no contrato; no subtipo MbaCase ele carrega as
  // Notas de Ensino, e o rótulo precisa acompanhar.
  const modelAnswerLabel = isTeachingNotesSubtype(details?.subtype)
    ? t('modal.reference.teachingNotes')
    : t('modal.reference.modelAnswer');

  const { mutate: submit, isPending: isSubmitting } = useSubmitCorrection(examPublishId);
  const { mutate: requestAiCorrection } = useAiCorrection();
  const { get: getBatchSuggestion, remove: removeBatchSuggestion } = useAiBatchSuggestions();

  /** Interrompe a contagem animada da nota, se estiver em curso. */
  function stopApplyAnimation() {
    if (gradeTweenRef.current !== null) {
      cancelAnimationFrame(gradeTweenRef.current);
      gradeTweenRef.current = null;
    }
  }

  // Pré-preenche completude, comentário e estado da IA ao carregar os detalhes da
  // resposta. Se houver uma sugestão de correção em lote pendente de revisão para
  // este `answerId`, ela chega pré-aplicada — nota e devolutiva já preenchidas,
  // como se o corretor tivesse clicado "Aplicar" — mas ele ainda precisa revisar e
  // salvar manualmente, exatamente como no fluxo individual. `getBatchSuggestion`
  // fica de fora das deps de propósito: só a troca de resposta ou o fim do
  // carregamento devem reinicializar o estado, não uma atualização do átomo de
  // lote disparada por outra questão.
  // biome-ignore lint/correctness/useExhaustiveDependencies: getBatchSuggestion é lido, mas não deve disparar o efeito de novo
  useEffect(() => {
    stopApplyAnimation();
    setAppliedAiGrade(null);
    setIsCommentLocked(false);
    appliedAiCommentRef.current = '';

    if (isDetailsLoading || !details) {
      setAiStatus('idle');
      setAiResult(null);
      return;
    }

    const baseCompleteness = clampCompleteness(details.completness ?? 0);
    const baseComment = details.comment ?? '';

    const batchSuggestion = getBatchSuggestion(currentAnswerId);
    if (!batchSuggestion) {
      setCompleteness(baseCompleteness);
      setComment(baseComment);
      setAiStatus('idle');
      setAiResult(null);
      return;
    }

    const grade =
      batchSuggestion.suggestedGrade !== undefined
        ? clampCompleteness(normalizeAiGrade(batchSuggestion.suggestedGrade))
        : baseCompleteness;
    const block = buildAiCorrectionComment(batchSuggestion);

    setCompleteness(grade);
    setComment(mergeAiCorrectionComment(baseComment, block, ''));
    setAppliedAiGrade(grade);
    setIsCommentLocked(Boolean(block));
    appliedAiCommentRef.current = block;
    setAiStatus('success');
    setAiResult(batchSuggestion);
  }, [details, isDetailsLoading, currentAnswerId]);

  // Nenhuma animação deve sobreviver ao fechamento do modal
  // biome-ignore lint/correctness/useExhaustiveDependencies: a limpeza só mexe em refs estáveis; recriar o efeito a cada render cancelaria a animação em curso
  useEffect(() => stopApplyAnimation, []);

  const isOpen = examPublishId !== null && examItemId !== null;
  // Salvar fica bloqueado enquanto a IA analisa: o resultado ainda pode alterar
  // nota e comentário, e gravar antes disso descartaria a análise em curso
  const isAiAnalyzing = aiStatus === 'loading';

  // ─── Navegação sequencial ──────────────────────────────────────────────────
  // A fila é a mesma lista da sub-tabela de alunos, na mesma ordem e incluindo as
  // respostas já corrigidas: "Próxima" caminha posição a posição até o fim, sem olhar
  // o status. A consulta pede uma resposta só (a da posição atual) e é o `totalCount`
  // dela que diz onde a fila termina. Não usamos o `hasNext` dos detalhes porque ele
  // considera apenas as pendentes.
  const { answer: queue } = useAnswerInfo(isOpen ? examPublishId : null, {
    ...(examItemId !== null ? { examItemId } : {}),
    pageNumber: position,
    pageSize: 1,
  });
  const totalAnswers = queue?.totalCount ?? 0;
  const hasNextAnswer = totalAnswers > 0 && position < totalAnswers;

  /**
   * Move o cursor para uma posição da fila e carrega a resposta que está lá.
   * Usa fetchQuery para pegar o ID direto do servidor, sem depender do cache — a
   * posição pode ter mudado de dono se outra pessoa corrigiu no meio do caminho.
   * Se a posição não existir mais, fecha o modal em vez de deixar a tela vazia.
   */
  async function navigateToPosition(next: number) {
    if (examPublishId === null || examItemId === null) return;
    setIsNavigating(true);
    try {
      const data = await queryClient.fetchQuery(
        getApiV1ExamCorrectionGridExampublishidQueryOptions(examPublishId, {
          examItemId,
          pageNumber: next,
          pageSize: 1,
        }),
      );
      const nextItem = data?.data?.[0];
      if (nextItem?.id) {
        setPosition(next);
        setCurrentAnswerId(nextItem.id);
        setCompleteness(0);
        setComment('');
      } else {
        onClose();
      }
    } catch {
      onClose();
    } finally {
      setIsNavigating(false);
    }
  }

  function handleAiCorrection() {
    if (!currentAnswerId) return;
    stopApplyAnimation();
    setAiStatus('loading');
    setAiResult(null);
    requestAiCorrection(
      { answerId: currentAnswerId },
      {
        onSuccess: data => {
          setAiResult(data);
          setAiStatus('success');
          // A conclusão é anunciada por toast: o resultado da análise é longo e um
          // banner no corpo do modal saía de vista com o scroll
          alert({
            alertType: 'success',
            title: t('modal.ai.successTitle'),
            description: t('modal.ai.successMessage'),
          });
        },
        onError: () => {
          setAiStatus('error');
        },
      },
    );
  }

  /**
   * Anima a completude do valor atual até a nota sugerida, para que a nota apareça
   * subindo em vez de saltar. Com `prefers-reduced-motion` o valor é aplicado direto.
   */
  function animateCompletenessTo(target: number) {
    const start = completeness;
    if (start === target || prefersReducedMotion()) {
      setCompleteness(target);
      return;
    }

    const startedAt = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / GRADE_TWEEN_DURATION_MS);
      // easing out cúbico — rápido no começo e desacelerando ao chegar na nota
      const eased = 1 - (1 - progress) ** 3;
      setCompleteness(clampCompleteness(start + (target - start) * eased));
      gradeTweenRef.current = progress < 1 ? requestAnimationFrame(step) : null;
    };
    gradeTweenRef.current = requestAnimationFrame(step);
  }

  /**
   * Aplica o resultado da IA à correção: a nota sugerida vai para a completude e a
   * justificativa vai para o comentário. O texto escrito manualmente é preservado, e
   * o bloco aplicado antes é substituído — reanalisar e aplicar de novo não duplica a
   * justificativa. A nota aplicada fica registrada para marcar o envio como assistido
   * por IA, enquanto o corretor não alterar a completude. O comentário passa a
   * somente-leitura para preservar o texto gerado — alterar a completude o destrava.
   *
   * A nota sobe animada até o valor sugerido, deixando visível o que a IA preencheu.
   */
  function handleApplyAiSuggestion() {
    if (!aiResult) return;

    stopApplyAnimation();

    if (aiResult.suggestedGrade !== undefined) {
      // A sugestão vem como fração de 0 a 1; a completude está na escala 0–100 e é
      // inteira, então a nota fracionária da IA é arredondada ao ser aplicada
      const grade = clampCompleteness(normalizeAiGrade(aiResult.suggestedGrade));
      animateCompletenessTo(grade);
      setAppliedAiGrade(grade);
    }

    const block = buildAiCorrectionComment(aiResult);

    setComment(current => mergeAiCorrectionComment(current, block, appliedAiCommentRef.current));
    appliedAiCommentRef.current = block;
    setIsCommentLocked(true);

    // A sugestão acabou de ser revisada e aplicada — não há mais motivo para manter
    // a tag "Sugestão da IA" na sub-tabela para esta resposta.
    removeBatchSuggestion(currentAnswerId);

    alert({ alertType: 'success', title: t('modal.ai.appliedTitle') });
  }

  /**
   * Altera a completude a partir do slider ou do input numérico, clampada em 0–100.
   * Mexer na nota devolve o comentário para edição: a devolutiva da IA foi escrita
   * para a nota sugerida, então ao divergir dela o corretor precisa poder ajustá-la.
   */
  function handleCompletenessChange(next: number) {
    // Um ajuste manual interrompe a contagem animada em vez de brigar com ela
    stopApplyAnimation();
    setCompleteness(clampCompleteness(next));
    setIsCommentLocked(false);
  }

  function handleNext() {
    void navigateToPosition(position + 1);
  }

  function handleSaveAndContinue() {
    if (!details || isAiAnalyzing) return;

    const savedAnswerId = currentAnswerId;

    submit(
      {
        data: buildCorrectionPayload({ details, completeness, comment, appliedAiGrade }),
      },
      {
        onSuccess: () => {
          // Invalida o cache desta resposta para exibir os valores atualizados na próxima abertura
          if (savedAnswerId) {
            void queryClient.invalidateQueries({
              queryKey: getApiV1ExamCorrectExamAnsweridQueryKey(savedAnswerId),
            });
          }
          // A resposta foi salva — mesmo que o corretor não tenha aplicado a sugestão
          // de IA (ex.: preferiu escrever a nota manualmente), ela deixou de estar
          // pendente e não deve mais aparecer com a tag "Sugestão da IA".
          removeBatchSuggestion(savedAnswerId);
          // No fim da fila o botão prometeu "Salvar e Fechar" — fecha sem buscar mais nada
          if (!hasNextAnswer) {
            onClose();
            return;
          }
          // Avança uma posição na fila, buscando o ID do servidor já atualizado
          void navigateToPosition(position + 1);
        },
      },
    );
  }

  return {
    // Resposta em exibição
    details,
    isOpen,
    isContentLoading,
    isAudio,
    isMultilineText,
    label: details?.label ?? questionItem?.label,
    student: details?.student,
    author: details?.author,
    value: details?.value,
    // Referência de correção
    subtypeLabel,
    modelAnswer: details?.modelAnswer?.trim(),
    modelAnswerLabel,
    keyTerms: parseKeyTerms(details?.keyTerms),
    // Correção
    completeness,
    comment,
    isCommentLocked,
    setComment,
    handleCompletenessChange,
    // IA
    aiStatus,
    aiResult,
    isAiAnalyzing,
    canAnalyze: currentAnswerId !== null,
    handleAiCorrection,
    handleApplyAiSuggestion,
    // Fila e envio
    hasNextAnswer,
    isNavigating,
    isSubmitting,
    handleNext,
    handleSaveAndContinue,
  } as const;
}
